import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner } from 'react-bootstrap';

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import HtmlHead from 'components/html-head/HtmlHead';
import { GetEventJudgeStore } from 'SessionState/Judge/EventJudgeStore';
import LayoutJudgePortal from 'layout/LayoutJudgePortal';
import JudgePortalShell from './JudgePortalShell';
import { JudgeDataContext } from 'data/JudgeDataProvider';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import JudgeSideAwardWinners from './JudgeSideAwardWinners';
import { LoadingOverlay } from 'data/TabulationDataProvider';
import TestJudgeTablet from './TestJudgeTablet';

type RouteParams = {
  eventJudgeId: string;
  eventId: string;
};

type ProgressSummary = {
  completed: number;
  total: number;
  percent: number;
};

type AgeGroupProgressSummary = ProgressSummary & {
  id: number | string;
  ageGroupId: number;
  name: string;
  contestants: number;
  divisionId: number;
  divisionName: string;
};

type DivisionProgressSummary = ProgressSummary & {
  id: number;
  name: string;
  contestants: number;
  ageGroups: AgeGroupProgressSummary[];
};

type AocProgressSummary = ProgressSummary & {
  id: number;
  name: string;
};

type ContestantAocProgressSummary = ProgressSummary & {
  aocId: number;
  aocName: string;
  entered: boolean;
  scoredRows: number;
  categories: number;
  statusLabel: string;
  statusVariant: string;
};

type ContestantProgressSummary = ProgressSummary & {
  id: number;
  contestantNumber: string;
  fullName: string;
  divisionId: number | null;
  divisionName: string;
  ageGroupName: string;
  aocs: ContestantAocProgressSummary[];
};

type HomeDashboardTab = 'aoc' | 'division' | 'ageGroup' | 'contestants';
type StartScoringWizardStep = 'aoc' | 'division' | 'ageGroup';

const getContestantId = (contestant: any): number => {
  return Number(
    contestant?.registeredContestantId ??
      contestant?.id ??
      contestant?.registeredPageantContestantId ??
      0,
  );
};

const getContestantNumber = (contestant: any): string => {
  return String(
    contestant?.contestantNumber ??
      contestant?.number ??
      contestant?.attributes?.contestantNumber ??
      '',
  );
};

const getContestantFullName = (contestant: any): string => {
  return String(
    contestant?.fullName ??
      contestant?.contestantName ??
      `${contestant?.firstName ?? ''} ${contestant?.lastName ?? ''}`.trim() ??
      'Contestant',
  ).trim();
};

const getContestantDivisionId = (contestant: any): number | null => {
  const raw =
    contestant?.attributes?.eventDivisionId ??
    contestant?.contestantAttributes?.eventDivisionId ??
    contestant?.divisionId ??
    contestant?.division?.id ??
    null;

  if (raw == null || raw === '') return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const getContestantAgeGroupName = (contestant: any): string => {
  return String(
    contestant?.attributes?.ageGroup ??
      contestant?.attributes?.ageGroupName ??
      contestant?.contestantAttributes?.ageGroup ??
      contestant?.contestantAttributes?.ageGroupName ??
      contestant?.ageGroupName ??
      contestant?.ageGroup ??
      '',
  );
};

const getDivisionId = (division: any): number => {
  return Number(
    division?.id ??
      division?.eventDivisionId ??
      division?.eventDivisionID ??
      division?.divisionId ??
      division?.pageantDivisionId ??
      division?.pageantAvailableDivisionId ??
      0,
  );
};

const getDivisionName = (division: any): string => {
  return String(
    division?.name ??
      division?.divisionName ??
      division?.eventDivisionName ??
      division?.pageantDivisionName ??
      'Division',
  );
};

const getAocCompetitionOrder = (aoc: any): number => {
  const raw =
    aoc?.competeOrder ??
    aoc?.competitionOrder ??
    aoc?.sortOrder ??
    aoc?.displayOrder ??
    aoc?.order ??
    Number.MAX_SAFE_INTEGER;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const normalizeAocIdFromScoreRow = (row: any): number => {
  return Number(row?.aocId ?? row?.pageantAvailableEventId ?? row?.pageantAvailableAocId ?? 0);
};

const normalizeCategoryIdFromScoreRow = (row: any): number => {
  return Number(row?.aocScoreCategoryId ?? row?.scoreCategoryId ?? row?.categoryId ?? 0);
};

const getScoreRowsForContestant = (allFetchedScores: any, contestantId: number): any[] => {
  return allFetchedScores?.[Number(contestantId)] ?? [];
};

const scoreRowMatchesJudge = (row: any, judgeId: number | string | null | undefined): boolean => {
  if (!judgeId) return true;

  const rawJudgeId = row?.judgeId ?? row?.eventJudgeId;
  if (rawJudgeId == null || rawJudgeId === '') return true;

  return Number(rawJudgeId) === Number(judgeId);
};

const getAocScoreCategories = (aoc: any): any[] => {
  return aoc?.aocScoringCategories ?? aoc?.aocScoreCategories ?? aoc?.scoreCategories ?? [];
};

const getAocTypeName = (aoc: any): string => {
  return String(
    aoc?.aocTypeName ??
      aoc?.areaOfCompetitionTypeName ??
      aoc?.areaOfCompetitionType?.name ??
      aoc?.typeName ??
      '',
  );
};

const JudgeEventHome = observer(() => {
  const title = 'Judge Portal';
  const description = 'Judge Event Home';

  const history = useHistory();
  const { eventJudgeId, eventId } = useParams<RouteParams>();
  const judgeStore = GetEventJudgeStore();

  const [isReady, setIsReady] = useState(false);

  const parsedEventJudgeId = Number(eventJudgeId);
  const parsedEventId = Number(eventId);

  const handleStartScoring = useCallback(
    (options?: {
      aocId?: number;
      divisionId?: number;
      ageGroupId?: number | string;
      contestantId?: number;
    }) => {
      const params = new URLSearchParams();

      if (options?.aocId) params.set('aocId', String(options.aocId));
      if (options?.divisionId) params.set('divisionId', String(options.divisionId));
      if (options?.ageGroupId) params.set('ageGroupId', String(options.ageGroupId));

      if (options?.contestantId) {
        params.set('contestantId', String(options.contestantId));

        history.push(
          `/judge/event/${parsedEventJudgeId}/${parsedEventId}/contestant-scoring?${params.toString()}`,
        );

        return;
      }

      history.push(`/judge/event/${parsedEventJudgeId}/${parsedEventId}/scoring?${params.toString()}`);
    },
    [history, parsedEventJudgeId, parsedEventId],
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-judge', 'home');

    return () => {
      document.documentElement.removeAttribute('data-judge');
    };
  }, []);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const judge = judgeStore.CurrentJudge;

  if (!isReady) {
    return (
      <>
        <HtmlHead title={title} description={description} image="" />
        <LoadingOverlay show={true} text="Loading" />
      </>
    );
  }

  if (!judge) {
    return <Redirect to="/judge/login" />;
  }

  if (judge.id !== parsedEventJudgeId || judge.eventId !== parsedEventId) {
    return <Redirect to="/judge/login" />;
  }

  return (
    <JudgePortalShell title="Judge Home" description="Judge Home">
      <JudgeDashboardContent judge={judge} onStartScoring={handleStartScoring} />
    </JudgePortalShell>
  );
});

type JudgeDashboardContentProps = {
  judge: any;
  onStartScoring: (options?: {
    aocId?: number;
    divisionId?: number;
    ageGroupId?: number | string;
    contestantId?: number;
  }) => void;
};

const JudgeDashboardContent: React.FC<JudgeDashboardContentProps> = ({ judge, onStartScoring }) => {
  const {
    judgeId,
    pageantAvailableAOC = [],
    ageGroupsOrdered = [],
    eventDivisions = [],
    allFetchedScores = {},
    isLoading,
    registeredContestants = [],
    isEnteredForAOC,
    updateJudgeProfile,
    pageantAvailableAwards = [],
    pageant,
    updateAwardRelationshipLocal,
    hasAgeGroups: contextHasAgeGroups,
  } = useContext(JudgeDataContext);

  const totalAocs = pageantAvailableAOC.length;
  const totalDivisions = eventDivisions.length;
  const totalAgeGroups = ageGroupsOrdered.length;
  const totalContestants = registeredContestants.length;
  const hasAgeGroups = contextHasAgeGroups ?? totalAgeGroups > 0;

  const [activeTab, setActiveTab] = useState<HomeDashboardTab>('aoc');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [tabletTestPassed, setTabletTestPassed] = useState(false);
  const [welcomeJudgeName, setWelcomeJudgeName] = useState(String(judge?.judgeName ?? ''));
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSavingWelcome, setIsSavingWelcome] = useState(false);
  const [isEditingJudgeName, setIsEditingJudgeName] = useState(false);
  const [judgeNameDraft, setJudgeNameDraft] = useState(String(judge?.judgeName ?? ''));
  const [isSavingJudgeName, setIsSavingJudgeName] = useState(false);

  const [expandedAocIds, setExpandedAocIds] = useState<Record<number, boolean>>({});

  const [contestantSearch, setContestantSearch] = useState('');
  const [contestantDivisionFilter, setContestantDivisionFilter] = useState('');
  const [contestantAgeGroupFilter, setContestantAgeGroupFilter] = useState('');
  const [contestantStatusFilter, setContestantStatusFilter] = useState<'all' | 'complete' | 'incomplete'>('all');

  const [activeAgeGroupDivisionId, setActiveAgeGroupDivisionId] = useState<number | null>(null);

  const [startScoringPrompt, setStartScoringPrompt] = useState<{
    show: boolean;
    step: StartScoringWizardStep;
    aocId: number | null;
    divisionId: number | null;
    ageGroupId: number | string | null;
  }>({
    show: false,
    step: 'aoc',
    aocId: null,
    divisionId: null,
    ageGroupId: null,
  });

  const [sideAwardModal, setSideAwardModal] = useState<{
    show: boolean;
    divisionId: number;
    ageGroup: any | null;
    contestants: any[];
  }>({
    show: false,
    divisionId: 0,
    ageGroup: null,
    contestants: [],
  });

  useEffect(() => {
    if (!hasAgeGroups && activeTab === 'ageGroup') {
      setActiveTab('division');
    }
  }, [hasAgeGroups, activeTab]);

  const hasAcceptedTerms = Boolean(
    judge?.agreedToTermsAt ??
      judge?.termsAcceptedAt ??
      judge?.judgeTermsAcceptedAt ??
      judge?.acceptedTermsAt ??
      judge?.hasAcceptedTerms ??
      judge?.termsAccepted,
  );

  const shouldShowWelcomeModal = useMemo(() => {
    const lastLoginCandidates = [
      judge?.lastLoggedIn,
      judge?.lastLoginAt,
      judge?.lastLoggedInAt,
      judge?.lastLoginDate,
    ];

    const hasLastLoginField = lastLoginCandidates.some((value) => value !== undefined);
    const lastLoggedIn = lastLoginCandidates.find((value) => value !== undefined) ?? null;
    const isExplicitFirstLogin = hasLastLoginField && !lastLoggedIn;

    return !hasAcceptedTerms || isExplicitFirstLogin;
  }, [judge, hasAcceptedTerms]);

  useEffect(() => {
    if (isLoading) return;

    if (shouldShowWelcomeModal) {
      setWelcomeJudgeName(String(judge?.judgeName ?? ''));
      setAcceptedTerms(hasAcceptedTerms);
      setShowWelcomeModal(true);
    }
  }, [isLoading, shouldShowWelcomeModal, judge?.judgeName, hasAcceptedTerms]);

  useEffect(() => {
    const currentName = String(judge?.judgeName ?? '');

    setWelcomeJudgeName(currentName);
    setJudgeNameDraft(currentName);
  }, [judge?.judgeName]);

  const getAocTypeSortOrder = (aoc: any): number => {
    const type = String(
      aoc?.areaOfCompetitionType?.name ??
        aoc?.areaOfCompetitionTypeName ??
        aoc?.aocTypeName ??
        aoc?.typeName ??
        '',
    ).toLowerCase();

    if (type.includes('stage')) return 1;
    if (type.includes('photo')) return 2;

    return 3;
  };

  const sortedAocs = useMemo(() => {
    return [...pageantAvailableAOC].sort((a: any, b: any) => {
      const typeDelta = getAocTypeSortOrder(a) - getAocTypeSortOrder(b);
      if (typeDelta !== 0) return typeDelta;

      const orderDelta = getAocCompetitionOrder(a) - getAocCompetitionOrder(b);
      if (orderDelta !== 0) return orderDelta;

      return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
    });
  }, [pageantAvailableAOC]);

  const sortedDivisions = useMemo(() => {
    return [...eventDivisions].sort((a: any, b: any) => getDivisionName(a).localeCompare(getDivisionName(b)));
  }, [eventDivisions]);

  const contestantDivisionLookup = useMemo(() => {
    const map = new Map<number, string>();

    eventDivisions.forEach((division: any) => {
      map.set(getDivisionId(division), getDivisionName(division));
    });

    return map;
  }, [eventDivisions]);

  const normalizedContestants = useMemo(() => {
    return (registeredContestants ?? [])
      .map((contestant: any) => {
        const id = getContestantId(contestant);
        const divisionId = getContestantDivisionId(contestant);

        return {
          ...contestant,
          id,
          fullName: getContestantFullName(contestant),
          contestantNumber: getContestantNumber(contestant),
          divisionId,
          divisionName:
            contestant?.attributes?.eventDivision ??
            contestant?.attributes?.eventDivisionName ??
            contestant?.contestantAttributes?.eventDivisionName ??
            contestantDivisionLookup.get(Number(divisionId)) ??
            '',
          ageGroupName: getContestantAgeGroupName(contestant),
        };
      })
      .sort((a: any, b: any) => {
        const aNum = Number(a.contestantNumber);
        const bNum = Number(b.contestantNumber);

        if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) return aNum - bNum;

        return String(a.fullName).localeCompare(String(b.fullName));
      });
  }, [registeredContestants, contestantDivisionLookup]);

  const isContestantCompleteForAoc = useCallback(
    (contestant: any, aoc: any): boolean => {
      const contestantId = getContestantId(contestant);
      const aocId = Number(aoc?.id ?? 0);

      if (!contestantId || !aocId || !isEnteredForAOC(contestant, aocId)) return false;

      const categories = getAocScoreCategories(aoc);

      const rows = getScoreRowsForContestant(allFetchedScores, contestantId).filter(
        (row) => normalizeAocIdFromScoreRow(row) === aocId && scoreRowMatchesJudge(row, judgeId),
      );

      if (categories.length === 0) {
        return rows.length > 0;
      }

      return categories.every((category: any) =>
        rows.some(
          (row) =>
            normalizeCategoryIdFromScoreRow(row) === Number(category?.id ?? 0) &&
            row?.score !== null &&
            row?.score !== undefined,
        ),
      );
    },
    [allFetchedScores, isEnteredForAOC, judgeId],
  );

  const eligibleContestantAocPairs = useMemo(() => {
    const pairs: Array<{
      contestantId: number;
      divisionId: number | null;
      divisionName: string;
      ageGroupName: string;
      aocId: number;
      aocName: string;
      contestant: any;
      aoc: any;
    }> = [];

    for (const contestant of normalizedContestants ?? []) {
      const contestantId = getContestantId(contestant);
      if (!contestantId) continue;

      const divisionId = getContestantDivisionId(contestant);
      const divisionName =
        contestant?.attributes?.eventDivision ??
        contestant?.attributes?.eventDivisionName ??
        contestant?.contestantAttributes?.eventDivisionName ??
        contestantDivisionLookup.get(Number(divisionId)) ??
        '';

      const ageGroupName = getContestantAgeGroupName(contestant);

      for (const aoc of sortedAocs ?? []) {
        const aocId = Number(aoc?.id ?? 0);
        if (!aocId) continue;

        if (isEnteredForAOC(contestant, aocId)) {
          pairs.push({
            contestantId,
            divisionId,
            divisionName,
            ageGroupName,
            aocId,
            aocName: aoc?.name ?? 'AOC',
            contestant,
            aoc,
          });
        }
      }
    }

    return pairs;
  }, [normalizedContestants, sortedAocs, contestantDivisionLookup, isEnteredForAOC]);

  const getAocProgressForPairs = useCallback(
    (pairs: Array<{ contestant: any; aoc: any }>): ProgressSummary => {
      const total = pairs.length;

      const completed = pairs.reduce((sum, pair) => {
        return sum + (isContestantCompleteForAoc(pair.contestant, pair.aoc) ? 1 : 0);
      }, 0);

      return {
        completed,
        total,
        percent: total === 0 ? 0 : Math.round((completed / total) * 100),
      };
    },
    [isContestantCompleteForAoc],
  );

  const overallProgress = useMemo(() => {
    return getAocProgressForPairs(eligibleContestantAocPairs);
  }, [eligibleContestantAocPairs, getAocProgressForPairs]);

  const progressByAoc: AocProgressSummary[] = useMemo(() => {
    return sortedAocs.map((aoc: any) => {
      const aocId = Number(aoc?.id ?? 0);
      const aocPairs = eligibleContestantAocPairs.filter((pair) => pair.aocId === aocId);
      const progress = getAocProgressForPairs(aocPairs);

      return {
        id: aocId,
        name: aoc?.name ?? 'AOC',
        ...progress,
      };
    });
  }, [sortedAocs, eligibleContestantAocPairs, getAocProgressForPairs]);

  const progressByContestant: ContestantProgressSummary[] = useMemo(() => {
    return normalizedContestants.map((contestant: any) => {
      const contestantId = getContestantId(contestant);

      const aocs: ContestantAocProgressSummary[] = sortedAocs.map((aoc: any) => {
        const aocId = Number(aoc?.id ?? 0);
        const entered = isEnteredForAOC(contestant, aocId);
        const categories = getAocScoreCategories(aoc);

        const rows = getScoreRowsForContestant(allFetchedScores, contestantId).filter(
          (row) => normalizeAocIdFromScoreRow(row) === aocId && scoreRowMatchesJudge(row, judgeId),
        );

        const scoredRows = categories.length
          ? categories.filter((category: any) =>
              rows.some(
                (row) =>
                  normalizeCategoryIdFromScoreRow(row) === Number(category?.id ?? 0) &&
                  row?.score !== null &&
                  row?.score !== undefined,
              ),
            ).length
          : rows.length;

        const isComplete = entered && isContestantCompleteForAoc(contestant, aoc);

        return {
          aocId,
          aocName: String(aoc?.name ?? 'AOC'),
          entered,
          completed: isComplete ? 1 : 0,
          total: entered ? 1 : 0,
          percent: !entered ? 0 : isComplete ? 100 : scoredRows > 0 ? 50 : 0,
          scoredRows,
          categories: categories.length,
          statusLabel: !entered
            ? 'NOT ENTERED'
            : isComplete
              ? 'SCORED COMPLETE'
              : scoredRows > 0
                ? 'PARTIAL'
                : 'ENTERED',
          statusVariant: !entered ? 'secondary' : isComplete ? 'success' : scoredRows > 0 ? 'warning' : 'info',
        };
      });

      const enteredAocs = aocs.filter((aoc) => aoc.entered);
      const completed = enteredAocs.filter((aoc) => aoc.completed > 0).length;
      const total = enteredAocs.length;

      return {
        id: contestantId,
        contestantNumber: String(contestant.contestantNumber ?? ''),
        fullName: String(contestant.fullName ?? 'Contestant'),
        divisionId: contestant.divisionId,
        divisionName: contestant.divisionName,
        ageGroupName: contestant.ageGroupName,
        aocs,
        completed,
        total,
        percent: total === 0 ? 0 : Math.round((completed / total) * 100),
      };
    });
  }, [normalizedContestants, sortedAocs, isEnteredForAOC, allFetchedScores, judgeId, isContestantCompleteForAoc]);

  const getDivisionContestants = useCallback(
    (divisionId: number): any[] => {
      return normalizedContestants.filter((contestant: any) => Number(contestant.divisionId) === Number(divisionId));
    },
    [normalizedContestants],
  );

  const getAgeGroupContestantsForDivision = useCallback(
    (ageGroup: any, divisionId: number): any[] => {
      if (!hasAgeGroups) {
        return getDivisionContestants(divisionId);
      }

      const rawContestants = ageGroup?.contestants ?? ageGroup?.registeredContestants ?? ageGroup?.pageantContestants ?? [];

      return rawContestants.filter((contestant: any) => getContestantDivisionId(contestant) === divisionId);
    },
    [hasAgeGroups, getDivisionContestants],
  );

  const progressByDivision: DivisionProgressSummary[] = useMemo(() => {
    return (eventDivisions ?? []).map((division: any) => {
      const divisionId = getDivisionId(division);
      const divisionName = getDivisionName(division);

      const divisionContestants = normalizedContestants.filter(
        (contestant: any) => Number(contestant.divisionId) === Number(divisionId),
      );

      const divisionContestantIds = new Set<number>(divisionContestants.map(getContestantId).filter(Boolean));

      const divisionPairs = eligibleContestantAocPairs.filter(
        (pair) => pair.divisionId === divisionId && divisionContestantIds.has(pair.contestantId),
      );

      const divisionProgress = getAocProgressForPairs(divisionPairs);

      if (!hasAgeGroups) {
        return {
          id: divisionId,
          name: divisionName,
          contestants: divisionContestantIds.size,
          ageGroups: [],
          ...divisionProgress,
        };
      }

      const divisionAgeGroups = (ageGroupsOrdered ?? []).filter(
        (ageGroup: any) => getAgeGroupContestantsForDivision(ageGroup, divisionId).length > 0,
      );

      const ageGroups: AgeGroupProgressSummary[] = divisionAgeGroups.map((ageGroup: any) => {
        const ageGroupId = Number(ageGroup?.ageGroupId ?? ageGroup?.id ?? 0);
        const ageGroupName = String(ageGroup?.ageGroupName ?? ageGroup?.name ?? ageGroup?.label ?? 'Age Group');

        const contestantsInAgeGroup = getAgeGroupContestantsForDivision(ageGroup, divisionId);
        const contestantIds = new Set<number>(contestantsInAgeGroup.map(getContestantId).filter(Boolean));

        const ageGroupPairs = eligibleContestantAocPairs.filter(
          (pair) => pair.divisionId === divisionId && contestantIds.has(pair.contestantId),
        );

        const progress = getAocProgressForPairs(ageGroupPairs);

        return {
          id: ageGroupId || ageGroupName,
          ageGroupId,
          name: ageGroupName,
          contestants: contestantIds.size,
          divisionId,
          divisionName,
          ...progress,
        };
      });

      return {
        id: divisionId,
        name: divisionName,
        contestants: divisionContestantIds.size,
        ageGroups,
        ...divisionProgress,
      };
    });
  }, [
    eventDivisions,
    normalizedContestants,
    eligibleContestantAocPairs,
    getAocProgressForPairs,
    hasAgeGroups,
    ageGroupsOrdered,
    getAgeGroupContestantsForDivision,
  ]);

  const getAgeGroupContestantIdSet = useCallback(
    (divisionId: number, ageGroupId: number | string): Set<number> => {
      const matchingAgeGroup = ageGroupsOrdered.find((ageGroup: any) => {
        const currentAgeGroupId = Number(ageGroup?.ageGroupId ?? ageGroup?.id ?? 0);
        const currentAgeGroupKey = currentAgeGroupId || ageGroup?.ageGroupName || ageGroup?.name;

        return String(currentAgeGroupKey) === String(ageGroupId);
      });

      const contestantIds = new Set<number>();

      if (matchingAgeGroup) {
        getAgeGroupContestantsForDivision(matchingAgeGroup, divisionId).forEach((contestant: any) => {
          const contestantId = getContestantId(contestant);
          if (contestantId) contestantIds.add(contestantId);
        });
      }

      return contestantIds;
    },
    [ageGroupsOrdered, getAgeGroupContestantsForDivision],
  );

  const getAgeGroupAocProgress = useCallback(
    (aocId: number, divisionId: number, ageGroupId: number | string): ProgressSummary => {
      const contestantIds = getAgeGroupContestantIdSet(divisionId, ageGroupId);

      const pairs = eligibleContestantAocPairs.filter(
        (pair) => pair.aocId === aocId && pair.divisionId === divisionId && contestantIds.has(pair.contestantId),
      );

      return getAocProgressForPairs(pairs);
    },
    [eligibleContestantAocPairs, getAgeGroupContestantIdSet, getAocProgressForPairs],
  );

  const getDivisionAocProgress = useCallback(
    (aocId: number, divisionId: number): ProgressSummary => {
      const pairs = eligibleContestantAocPairs.filter(
        (pair) => pair.aocId === aocId && pair.divisionId === divisionId,
      );

      return getAocProgressForPairs(pairs);
    },
    [eligibleContestantAocPairs, getAocProgressForPairs],
  );

  const aocDashboardCards = useMemo(() => {
    return sortedAocs.map((aoc: any) => {
      const aocId = Number(aoc?.id ?? 0);

      const aocProgress =
        progressByAoc.find((progress) => Number(progress.id) === aocId) ?? {
          id: aocId,
          name: aoc?.name ?? 'AOC',
          completed: 0,
          total: 0,
          percent: 0,
        };

      return {
        ...aocProgress,
        divisions: progressByDivision
          .map((division) => {
            if (!hasAgeGroups) {
              return {
                ...division,
                ...getDivisionAocProgress(aocId, division.id),
                ageGroups: [],
              };
            }

            return {
              ...division,
              ageGroups: division.ageGroups
                .map((ageGroup) => ({
                  ...ageGroup,
                  ...getAgeGroupAocProgress(aocId, division.id, ageGroup.ageGroupId || ageGroup.id),
                }))
                .filter((ageGroup) => ageGroup.total > 0),
            };
          })
          .filter((division) => (hasAgeGroups ? division.ageGroups.length > 0 : division.total > 0)),
      };
    });
  }, [
    sortedAocs,
    progressByAoc,
    progressByDivision,
    hasAgeGroups,
    getDivisionAocProgress,
    getAgeGroupAocProgress,
  ]);

  const filteredContestants = useMemo(() => {
    const query = contestantSearch.trim().toLowerCase();

    return progressByContestant.filter((contestant) => {
      const matchesSearch =
        !query ||
        contestant.fullName.toLowerCase().includes(query) ||
        contestant.contestantNumber.toLowerCase().includes(query);

      const matchesDivision =
        !contestantDivisionFilter || String(contestant.divisionId ?? '') === contestantDivisionFilter;

      const matchesAgeGroup = !hasAgeGroups || !contestantAgeGroupFilter || contestant.ageGroupName === contestantAgeGroupFilter;

      return matchesSearch && matchesDivision && matchesAgeGroup;
    });
  }, [
    progressByContestant,
    contestantSearch,
    contestantDivisionFilter,
    contestantAgeGroupFilter,
    hasAgeGroups,
  ]);

  const selectedStartAoc = useMemo(() => {
    return sortedAocs.find((aoc: any) => Number(aoc?.id ?? 0) === Number(startScoringPrompt.aocId)) ?? null;
  }, [sortedAocs, startScoringPrompt.aocId]);

  const selectedStartDivision = useMemo(() => {
    return progressByDivision.find((division) => Number(division.id) === Number(startScoringPrompt.divisionId)) ?? null;
  }, [progressByDivision, startScoringPrompt.divisionId]);

  const selectedStartAgeGroups = useMemo(() => {
    return selectedStartDivision?.ageGroups ?? [];
  }, [selectedStartDivision]);

  const selectedStartAocDivisions = useMemo(() => {
    const selectedAocId = Number(startScoringPrompt.aocId ?? 0);

    return progressByDivision.map((division) => {
      if (!hasAgeGroups) {
        return {
          ...division,
          ...getDivisionAocProgress(selectedAocId, division.id),
          ageGroups: [],
        };
      }

      const ageGroups = division.ageGroups.map((ageGroup) => ({
        ...ageGroup,
        ...getAgeGroupAocProgress(selectedAocId, division.id, ageGroup.ageGroupId || ageGroup.id),
      }));

      const completed = ageGroups.reduce((sum, ageGroup) => sum + Number(ageGroup.completed ?? 0), 0);
      const total = ageGroups.reduce((sum, ageGroup) => sum + Number(ageGroup.total ?? 0), 0);

      return {
        ...division,
        ageGroups,
        completed,
        total,
        percent: total === 0 ? 0 : Math.round((completed / total) * 100),
      };
    });
  }, [
    progressByDivision,
    hasAgeGroups,
    getDivisionAocProgress,
    getAgeGroupAocProgress,
    startScoringPrompt.aocId,
  ]);

  const getProgressBadgeClassName = (completed: number, total: number): string => {
    if (total === 0 || completed === 0) return 'bg-muted';
    if (completed >= total) return 'bg-success';

    return 'bg-warning';
  };

  const getProgressPercent = (completed: number, total: number): number => {
    if (total === 0) return 0;

    return Math.round((completed / total) * 100);
  };

  const activeExpandedAocId = useMemo(() => {
    const key = Object.keys(expandedAocIds).find((id) => expandedAocIds[Number(id)]);

    return key ? Number(key) : null;
  }, [expandedAocIds]);

  const activeExpandedAoc = useMemo(() => {
    if (!activeExpandedAocId) return null;

    return aocDashboardCards.find((aoc) => Number(aoc.id) === activeExpandedAocId) ?? null;
  }, [activeExpandedAocId, aocDashboardCards]);

  const getRingColor = (percent: number): string => {
    if (percent >= 100) return 'var(--success, #198754)';
    if (percent > 0) return 'var(--warning, #ffc107)';

    return 'var(--muted, #6c757d)';
  };

  const selectAocTimelineItem = (aocId: number) => {
    setExpandedAocIds({ [aocId]: true });
  };

const getAocTimelineType = (
  aoc: any,
): 'stage' | 'photo' | 'preCompetition' => {
  const typeName = getAocTypeName(aoc).toLowerCase();

  if (typeName.includes('pre')) {
    return 'preCompetition';
  }

  return typeName.includes('photo') ? 'photo' : 'stage';
};

  type AocTimelineType = 'stage' | 'photo' | 'preCompetition';

const [aocTimelineType, setAocTimelineType] = useState<AocTimelineType>('stage');
  const aocTimelineItemRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const filteredTimelineAocs = useMemo(() => {
    return aocDashboardCards.filter((aoc) => {
      const rawAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(aoc.id));

      return getAocTimelineType(rawAoc) === aocTimelineType;
    });
  }, [aocDashboardCards, sortedAocs, aocTimelineType]);

  useEffect(() => {
    if (!activeExpandedAocId) return;

    window.requestAnimationFrame(() => {
      aocTimelineItemRefs.current[activeExpandedAocId]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    });
  }, [activeExpandedAocId, aocTimelineType]);

  const getAocCompeteOrderValue = useCallback((aoc: any): number => {
  const raw =
    aoc?.competeOrder ??
    aoc?.competitionOrder ??
    aoc?.sortOrder ??
    aoc?.displayOrder ??
    aoc?.order ??
    0;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}, []);

const getAllTimelineAocsInCompeteOrder = useCallback(() => {
  return [...(aocDashboardCards ?? [])].sort((a: any, b: any) => {
    const rawA = sortedAocs.find(
      (item: any) => Number(item?.id) === Number(a.id),
    );
    const rawB = sortedAocs.find(
      (item: any) => Number(item?.id) === Number(b.id),
    );

    const orderA = getAocCompeteOrderValue(rawA);
    const orderB = getAocCompeteOrderValue(rawB);

    if (orderA !== orderB) return orderA - orderB;

    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
  });
}, [aocDashboardCards, sortedAocs, getAocCompeteOrderValue]);

const selectAocFromAllTimeline = useCallback(
  (aocId: number) => {
    const rawAoc = sortedAocs.find(
      (item: any) => Number(item?.id) === Number(aocId),
    );

    setAocTimelineType(getAocTimelineType(rawAoc));
    setExpandedAocIds({ [Number(aocId)]: true });
  },
  [sortedAocs, getAocTimelineType],
);

const goToNextAoc = useCallback(() => {
  if (!activeExpandedAoc) return;

  const orderedAocs = getAllTimelineAocsInCompeteOrder();
  if (!orderedAocs.length) return;

  const currentIndex = orderedAocs.findIndex(
    (aoc: any) => Number(aoc.id) === Number(activeExpandedAoc.id),
  );

  const nextIndex =
    currentIndex < 0 || currentIndex >= orderedAocs.length - 1
      ? 0
      : currentIndex + 1;

  selectAocFromAllTimeline(Number(orderedAocs[nextIndex].id));
}, [
  activeExpandedAoc,
  getAllTimelineAocsInCompeteOrder,
  selectAocFromAllTimeline,
]);

const goToPreviousAoc = useCallback(() => {
  if (!activeExpandedAoc) return;

  const orderedAocs = getAllTimelineAocsInCompeteOrder();
  if (!orderedAocs.length) return;

  const currentIndex = orderedAocs.findIndex(
    (aoc: any) => Number(aoc.id) === Number(activeExpandedAoc.id),
  );

  const previousIndex =
    currentIndex <= 0 ? orderedAocs.length - 1 : currentIndex - 1;

  selectAocFromAllTimeline(Number(orderedAocs[previousIndex].id));
}, [
  activeExpandedAoc,
  getAllTimelineAocsInCompeteOrder,
  selectAocFromAllTimeline,
]);

  const getAocTypeCompletion = (
  type: 'stage' | 'photo' | 'preCompetition',
) => {
    const typeAocs = aocDashboardCards.filter((aoc) => {
      const rawAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(aoc.id));

      return getAocTimelineType(rawAoc) === type;
    });

    const total = typeAocs.length;
    const completed = typeAocs.filter((aoc) => aoc.percent >= 100).length;

    return {
      total,
      completed,
      isComplete: total > 0 && completed === total,
    };
  };

  const stageCompletion = getAocTypeCompletion('stage');
  const photoCompletion = getAocTypeCompletion('photo');
  const preCompetitionCompletion = getAocTypeCompletion('preCompetition');

  const handleAcceptWelcome = useCallback(async () => {
    if (!welcomeJudgeName.trim() || !acceptedTerms) return;

    setIsSavingWelcome(true);

    try {
      const updatedJudge = await updateJudgeProfile({
        judgeName: welcomeJudgeName.trim(),
        acceptedTerms: true,
      });

      if (updatedJudge) {
        setShowWelcomeModal(false);
      }
    } catch (error) {
      console.error('Unable to save judge welcome acknowledgement:', error);
    } finally {
      setIsSavingWelcome(false);
    }
  }, [welcomeJudgeName, acceptedTerms, updateJudgeProfile]);

  const handleSaveJudgeName = useCallback(async () => {
    const trimmedName = judgeNameDraft.trim();
    if (!trimmedName) return;

    setIsSavingJudgeName(true);

    try {
      const updatedJudge = await updateJudgeProfile({
        judgeName: trimmedName,
        acceptedTerms: true,
      });

      setWelcomeJudgeName(trimmedName);
      setJudgeNameDraft(trimmedName);

      if (updatedJudge) {
        const judgeStore = GetEventJudgeStore();

        judgeStore.CurrentJudge = {
          ...judgeStore.CurrentJudge,
          ...updatedJudge,
          judgeName: trimmedName,
        };
      }

      setIsEditingJudgeName(false);
    } catch (error) {
      console.error('Unable to update judge name:', error);
    } finally {
      setIsSavingJudgeName(false);
    }
  }, [judgeNameDraft, updateJudgeProfile]);

  const statBoxClassName = (tab: HomeDashboardTab): string => {
    const isActive = activeTab === tab;

    return [
      'border rounded p-3 h-100 position-relative cursor-pointer judge-progress-stat-box',
      isActive ? 'border-primary shadow bg-primary text-white' : 'bg-foreground',
    ]
      .filter(Boolean)
      .join(' ');
  };

  const openStartScoringPrompt = useCallback(() => {
    setStartScoringPrompt({
      show: true,
      step: 'aoc',
      aocId: null,
      divisionId: null,
      ageGroupId: null,
    });
  }, []);

  const closeStartScoringPrompt = useCallback(() => {
    setStartScoringPrompt({
      show: false,
      step: 'aoc',
      aocId: null,
      divisionId: null,
      ageGroupId: null,
    });
  }, []);

  const handleWizardAocSelect = useCallback((aocId: number) => {
    setStartScoringPrompt((prev) => ({
      ...prev,
      aocId,
      divisionId: null,
      ageGroupId: null,
      step: 'division',
    }));
  }, []);

  const handleWizardDivisionSelect = useCallback(
    (divisionId: number) => {
      if (!startScoringPrompt.aocId) return;

      if (!hasAgeGroups) {
        onStartScoring({
          aocId: startScoringPrompt.aocId,
          divisionId,
        });

        closeStartScoringPrompt();
        return;
      }

      setStartScoringPrompt((prev) => ({
        ...prev,
        divisionId,
        ageGroupId: null,
        step: 'ageGroup',
      }));
    },
    [hasAgeGroups, startScoringPrompt.aocId, onStartScoring, closeStartScoringPrompt],
  );

  const handleWizardAgeGroupSelect = useCallback(
    (ageGroupId: number | string) => {
      if (!startScoringPrompt.aocId || !startScoringPrompt.divisionId) return;

      onStartScoring({
        aocId: startScoringPrompt.aocId,
        divisionId: startScoringPrompt.divisionId,
        ageGroupId,
      });

      closeStartScoringPrompt();
    },
    [startScoringPrompt.aocId, startScoringPrompt.divisionId, onStartScoring, closeStartScoringPrompt],
  );

  const handleStartFromAocAgeGroup = useCallback(
    (aocId: number, divisionId: number, ageGroupId: number | string) => {
      onStartScoring({ aocId, divisionId, ageGroupId });
    },
    [onStartScoring],
  );

  const startScoringHeaderTitle = (() => {
    const aocLabel = selectedStartAoc?.name ?? (startScoringPrompt.step === 'aoc' ? 'Pick An Event' : 'AOC');

    const divisionLabel =
      selectedStartDivision?.name ?? (startScoringPrompt.step === 'division' ? 'Pick Division' : 'Division');

    const ageGroupLabel = startScoringPrompt.step === 'ageGroup' ? 'Pick An Age Group' : 'Age Group';

    return (
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className={startScoringPrompt.step === 'aoc' ? 'fw-bold fs-4' : ''}>{aocLabel}</span>

        {startScoringPrompt.step !== 'aoc' && (
          <>
            <span className="text-muted">&gt;</span>
            <span className={startScoringPrompt.step === 'division' ? 'fw-bold fs-4' : ''}>{divisionLabel}</span>
          </>
        )}

        {startScoringPrompt.step === 'ageGroup' && (
          <>
            <span className="text-muted">&gt;</span>
            <span className="fw-bold fs-4">{ageGroupLabel}</span>
          </>
        )}
      </div>
    );
  })();

  const startScoringSelectionModal = (
    <Modal show={startScoringPrompt.show} onHide={closeStartScoringPrompt} centered size="lg" animation={false}>
      <Modal.Header closeButton>
        <Modal.Title>{startScoringHeaderTitle}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {startScoringPrompt.step === 'aoc' && (
          <>
            <div className="mb-3">
              <div className="text-muted small">Step 1 of {hasAgeGroups ? 3 : 2}</div>
            </div>

            <Row className="g-2 row-cols-1 row-cols-md-2">
              {sortedAocs.map((aoc: any) => {
                const aocId = Number(aoc?.id ?? 0);
                const progress =
                  progressByAoc.find((item) => Number(item.id) === aocId) ?? {
                    completed: 0,
                    total: 0,
                    percent: 0,
                  };

                const percent = getProgressPercent(progress.completed, progress.total);

                return (
                  <Col key={aocId}>
                    <Button
                      variant="outline-body"
                      className="w-100 text-start p-3 h-100 position-relative"
                      onClick={() => handleWizardAocSelect(aocId)}
                    >
                      <span
                        className={`badge position-absolute top-0 end-0 m-2 ${getProgressBadgeClassName(
                          progress.completed,
                          progress.total,
                        )}`}
                      >
                        {progress.total === 0 ? 'NO ENTRIES' : `${percent}%`}
                      </span>

                      <div className="fw-bold text-truncate p-2 fs-3">{aoc.name}</div>
                    </Button>
                  </Col>
                );
              })}
            </Row>
          </>
        )}

        {startScoringPrompt.step === 'division' && (
          <>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
              <div className="text-muted small">Step 2 of {hasAgeGroups ? 3 : 2}</div>

              <Button
                variant="outline-body"
                size="sm"
                onClick={() => setStartScoringPrompt((prev) => ({ ...prev, step: 'aoc' }))}
              >
                Back to Events
              </Button>
            </div>

            <Row className="g-2 row-cols-1 row-cols-md-2">
              {selectedStartAocDivisions.map((division) => {
                const percent = getProgressPercent(division.completed, division.total);

                return (
                  <Col key={division.id}>
                    <Button
                      variant="outline-body"
                      className="w-100 text-start p-3 h-100 position-relative"
                      onClick={() => handleWizardDivisionSelect(division.id)}
                      disabled={division.total === 0}
                    >
                      <span
                        className={`badge position-absolute top-0 end-0 m-2 ${getProgressBadgeClassName(
                          division.completed,
                          division.total,
                        )}`}
                      >
                        {division.total === 0 ? 'NO ENTRIES' : `${percent}%`}
                      </span>

                      <div className="fw-bold text-truncate p-2 fs-3">{division.name}</div>

                      <div className="text-muted small px-2">
                        {division.completed}/{division.total} complete
                      </div>
                    </Button>
                  </Col>
                );
              })}
            </Row>
          </>
        )}

        {startScoringPrompt.step === 'ageGroup' && (
          <>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
              <div className="text-muted small">Step 3 of 3</div>

              <Button
                variant="outline-body"
                size="sm"
                onClick={() => setStartScoringPrompt((prev) => ({ ...prev, step: 'division' }))}
              >
                Back to Divisions
              </Button>
            </div>

            <Row className="g-2 row-cols-2 row-cols-md-3">
              {selectedStartAgeGroups.map((ageGroup) => {
                const selectedDivisionForAoc = selectedStartAocDivisions.find(
                  (division) => Number(division.id) === Number(startScoringPrompt.divisionId),
                );

                const aocAgeGroupProgress =
                  selectedDivisionForAoc?.ageGroups.find(
                    (item) => String(item.ageGroupId || item.id) === String(ageGroup.ageGroupId || ageGroup.id),
                  ) ?? {
                    completed: 0,
                    total: 0,
                    percent: 0,
                  };

                return (
                  <Col key={`${ageGroup.divisionId}-${String(ageGroup.id)}`}>
                    <Button
                      variant="outline-body"
                      className="w-100 text-start p-3 h-100 position-relative"
                      onClick={() => handleWizardAgeGroupSelect(ageGroup.ageGroupId || ageGroup.id)}
                      disabled={aocAgeGroupProgress.total === 0}
                    >
                      <span
                        className={`badge position-absolute top-0 end-0 m-2 ${getProgressBadgeClassName(
                          aocAgeGroupProgress.completed,
                          aocAgeGroupProgress.total,
                        )}`}
                      >
                        {aocAgeGroupProgress.total === 0 ? 'NO ENTRIES' : `${aocAgeGroupProgress.percent}%`}
                      </span>

                      <div className="fw-bold text-truncate p-2 fs-3">{ageGroup.name}</div>
                    </Button>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </Modal.Body>
    </Modal>
  );

  const progressBarVariant = (percent: number, total: number): string => {
    if (total === 0) return 'secondary';
    if (percent === 100) return 'success';
    if (percent > 0) return 'warning';

    return 'secondary';
  };

  const getPercentTextClassName = (percent: number): string => {
    if (percent >= 100) return 'text-success';
    if (percent > 0) return 'text-warning';

    return 'text-muted';
  };

  const getCategoryName = (category: any): string =>
    String(
      category?.scoringCategoryName ??
        category?.scoringCategory?.name ??
        category?.scoringCategory?.Name ??
        category?.name ??
        'Category',
    );

  const getCategoryMaxScore = (category: any): number => {
    const parsed = Number(category?.maxScore ?? category?.scoringCategory?.maxScore ?? 0);

    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getPlusCount = (score: number, maxScore: number): number => {
    if (!maxScore || score <= maxScore) return 0;

    return Math.round((score - maxScore) * 10);
  };

  const formatScoreWithPluses = (score: number | null | undefined, maxScore: number): string => {
    if (score === null || score === undefined) return '-';

    const plusCount = getPlusCount(Number(score), maxScore);

    return `${score}${plusCount > 0 ? ` (${'+'.repeat(plusCount)})` : ''}`;
  };

  const getScoreValue = (row: any): string => (row?.score === null || row?.score === undefined ? '' : String(row.score));

  const getScoreExportRows = useCallback(() => {
    const rows: Array<{
      contestantNumber: string;
      contestantName: string;
      division: string;
      ageGroup: string;
      aoc: string;
      category: string;
      score: string;
      judgeLabel: string;
      judgeName: string;
      eventName: string;
    }> = [];

    normalizedContestants.forEach((contestant: any) => {
      const contestantId = getContestantId(contestant);

      sortedAocs.forEach((aoc: any) => {
        const aocId = Number(aoc?.id ?? 0);

        if (!isEnteredForAOC(contestant, aocId)) return;

        const categories = getAocScoreCategories(aoc);
        const scoreRows = getScoreRowsForContestant(allFetchedScores, contestantId).filter(
          (row) => normalizeAocIdFromScoreRow(row) === aocId && scoreRowMatchesJudge(row, judgeId),
        );

        categories.forEach((category: any) => {
          const categoryId = Number(category?.id ?? 0);
          const scoreRow = scoreRows.find((row) => normalizeCategoryIdFromScoreRow(row) === categoryId);

          rows.push({
            contestantNumber: getContestantNumber(contestant),
            contestantName: getContestantFullName(contestant),
            division: contestant.divisionName || '',
            ageGroup: contestant.ageGroupName || '',
            aoc: String(aoc?.name ?? 'AOC'),
            category: getCategoryName(category),
            score: getScoreValue(scoreRow),
            judgeLabel: String(judge?.judgeLabel ?? ''),
            judgeName: String(welcomeJudgeName || judge?.judgeName || ''),
            eventName: String(judge?.eventName ?? pageant?.event?.name ?? 'Event'),
          });
        });
      });
    });

    return rows;
  }, [
    normalizedContestants,
    sortedAocs,
    isEnteredForAOC,
    allFetchedScores,
    judgeId,
    judge,
    welcomeJudgeName,
    pageant,
  ]);

  const getContestantAocExportStatus = (
    contestant: any,
    aoc: any,
  ): { label: 'NOT ENTERED' | 'INCOMPLETE' | 'COMPLETE'; percent: number } => {
    const contestantId = getContestantId(contestant);
    const aocId = Number(aoc?.id ?? 0);
    const entered = isEnteredForAOC(contestant, aocId);
    const categories = getAocScoreCategories(aoc);

    if (!entered) return { label: 'NOT ENTERED', percent: 0 };

    const scoreRows = getScoreRowsForContestant(allFetchedScores, contestantId).filter(
      (row) => normalizeAocIdFromScoreRow(row) === aocId && scoreRowMatchesJudge(row, judgeId),
    );

    const scoredCount = categories.filter((category: any) =>
      scoreRows.some(
        (row) =>
          normalizeCategoryIdFromScoreRow(row) === Number(category?.id ?? 0) &&
          row?.score !== null &&
          row?.score !== undefined,
      ),
    ).length;

    const percent = categories.length === 0 ? 0 : Math.round((scoredCount / categories.length) * 100);

    return {
      label: percent === 100 ? 'COMPLETE' : 'INCOMPLETE',
      percent,
    };
  };

  const handleExportJudgeScores = useCallback(async () => {
    const rows = getScoreExportRows();
    const eventName = String(judge?.eventName ?? pageant?.event?.name ?? 'Event');
    const judgeLabel = String(judge?.judgeLabel ?? 'Judge');
    const judgeName = String(welcomeJudgeName || judge?.judgeName || 'Judge');

    const safeFileName = (value: string): string => value.replace(/[^\w\d-]+/g, '_').replace(/^_+|_+$/g, '');

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(
      rows.map((row) => ({
        'Contestant #': row.contestantNumber,
        Contestant: row.contestantName,
        Division: row.division,
        'Age Group': row.ageGroup,
        AOC: row.aoc,
        Category: row.category,
        Score: row.score,
        'Judge Label': row.judgeLabel,
        'Judge Name': row.judgeName,
        Event: row.eventName,
      })),
    );

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Judge Scores');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const zip = new JSZip();

    zip.file(`${safeFileName(eventName)}_${safeFileName(judgeLabel)}_scores.xlsx`, excelBuffer);

    normalizedContestants.forEach((contestant: any) => {
      const contestantRows = rows.filter((row) => row.contestantNumber === getContestantNumber(contestant));

      if (contestantRows.length === 0) return;

      const doc = new jsPDF();

      doc.setFontSize(13);
      doc.text(`${eventName}`, 10, 10);

      doc.setFontSize(10);
      doc.text(`#${getContestantNumber(contestant)} ${getContestantFullName(contestant)}`, 10, 17);

      doc.setFontSize(7);
      doc.text(`Judge: ${judgeLabel} - ${judgeName}`, 10, 22);
      doc.text(
        hasAgeGroups
          ? `Division: ${contestant.divisionName || ''}   Age Group: ${contestant.ageGroupName || ''}`
          : `Division: ${contestant.divisionName || ''}`,
        10,
        26,
      );

      let currentY = 33;
      const contestantId = getContestantId(contestant);
      const notEnteredAocs: string[] = [];

      sortedAocs.forEach((aoc: any) => {
        const aocId = Number(aoc?.id ?? 0);
        const categories = getAocScoreCategories(aoc);
        const status = getContestantAocExportStatus(contestant, aoc);

        if (status.label === 'NOT ENTERED') {
          notEnteredAocs.push(String(aoc?.name ?? 'AOC'));
          return;
        }

        const scoreRows = getScoreRowsForContestant(allFetchedScores, contestantId).filter(
          (row) => normalizeAocIdFromScoreRow(row) === aocId && scoreRowMatchesJudge(row, judgeId),
        );

        const notes = String(scoreRows.find((row: any) => String(row?.notes ?? '').trim())?.notes ?? '').trim();

        if (currentY > 185) {
          doc.addPage();
          currentY = 10;
        }

        const categoryScores = categories.map((category: any) => {
          const row = scoreRows.find(
            (scoreRow) => normalizeCategoryIdFromScoreRow(scoreRow) === Number(category?.id ?? 0),
          );

          const score = row?.score === null || row?.score === undefined ? null : Number(row.score);
          const maxScore = getCategoryMaxScore(category);
          const plusCount = score === null ? 0 : getPlusCount(score, maxScore);

          return {
            score,
            maxScore,
            plusCount,
            displayScore: formatScoreWithPluses(score, maxScore),
          };
        });

        const totalScore = categoryScores.reduce((sum, item) => sum + Number(item.score ?? 0), 0);
        const totalMax = categoryScores.reduce((sum, item) => sum + Number(item.maxScore ?? 0), 0);
        const totalPluses = categoryScores.reduce((sum, item) => sum + item.plusCount, 0);

        const pageWidth = doc.internal.pageSize.getWidth();
        const left = 10;
        const right = 10;
        const tableWidth = pageWidth - left - right;

        doc.setDrawColor(33, 37, 41);
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(left, currentY, tableWidth, 7, 1.5, 1.5, 'FD');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 37, 41);
        doc.text(String(aoc?.name ?? 'AOC'), left + 2, currentY + 4.8);

        doc.setFontSize(10);
        doc.text(
          `${totalScore.toFixed(1).replace(/\.0$/, '')}/${totalMax.toFixed(1).replace(/\.0$/, '')}`,
          pageWidth - right - 2,
          currentY + 4.8,
          { align: 'right' },
        );

        autoTable(doc, {
          startY: currentY + 7,
          head: [[...categories.map((category: any) => getCategoryName(category)), 'Pluses']],
          body: [[...categoryScores.map((item) => item.displayScore), totalPluses > 0 ? '+'.repeat(totalPluses) : '-']],
          theme: 'grid',
          margin: { left, right },
          styles: {
            fontSize: 7,
            halign: 'center',
            cellPadding: 1.4,
            overflow: 'linebreak',
          },
          headStyles: {
            fillColor: [33, 37, 41],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 7,
            cellPadding: 1.3,
          },
          bodyStyles: {
            fontSize: 8,
            fontStyle: 'bold',
            cellPadding: 1.4,
          },
        });

        currentY = (doc as any).lastAutoTable.finalY;

        if (notes) {
          const noteLines = doc.splitTextToSize(notes, tableWidth - 4);
          const noteHeight = Math.max(8, noteLines.length * 4 + 5);

          doc.setDrawColor(210, 210, 210);
          doc.setFillColor(252, 252, 252);
          doc.roundedRect(left, currentY + 1, tableWidth, noteHeight, 1.5, 1.5, 'FD');

          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.text('NOTES', left + 2, currentY + 5);

          doc.setFont('helvetica', 'normal');
          doc.text(noteLines, left + 18, currentY + 5);

          currentY += noteHeight + 6;
        } else {
          currentY += 5;
        }
      });

      if (notEnteredAocs.length > 0) {
        if (currentY > 185) {
          doc.addPage();
          currentY = 12;
        }

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 37, 41);

        currentY += 4;
        doc.text('NOT ENTERED AREAS OF COMPETITION', 10, currentY);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(notEnteredAocs.join(', '), 10, currentY + 5);
      }

      const pdfBlob = doc.output('blob');

      zip.file(`${safeFileName(getContestantNumber(contestant))}_${safeFileName(getContestantFullName(contestant))}.pdf`, pdfBlob);
    });

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    saveAs(zipBlob, `${safeFileName(eventName)}_${safeFileName(judgeLabel)}_export.zip`);
  }, [
    getScoreExportRows,
    judge,
    pageant,
    welcomeJudgeName,
    normalizedContestants,
    sortedAocs,
    allFetchedScores,
    judgeId,
    isEnteredForAOC,
    hasAgeGroups,
  ]);

  const getAwardRelationshipsForAward = (award: any): any[] => {
    return award?.awardRelationships ?? award?.registeredContestantAwardRelationships ?? [];
  };

  const getAwardId = (award: any): number => {
    return Number(award?.id ?? award?.pageantAvailableTitleId ?? award?.pageantAvailableAwardId ?? 0);
  };

  const getAwardName = (award: any): string => {
    return String(award?.name ?? award?.awardName ?? 'Side Award');
  };

  const getSideAwardRowsForContestants = (contestants: any[]) => {
    const contestantMap = new Map<number, any>();

    contestants.forEach((contestant: any) => {
      contestantMap.set(getContestantId(contestant), contestant);
    });

    return (pageantAvailableAwards ?? []).map((award: any) => {
      const relationships = getAwardRelationshipsForAward(award);

      const winners = relationships
        .filter((relationship: any) => {
          const contestantId = Number(relationship?.registeredPageantContestantId ?? 0);

          return relationship?.isWon === true && contestantMap.has(contestantId);
        })
        .map((relationship: any) => {
          const contestant = contestantMap.get(Number(relationship?.registeredPageantContestantId ?? 0));

          return `#${getContestantNumber(contestant)} ${getContestantFullName(contestant)}`;
        });

      return {
        awardId: getAwardId(award),
        awardName: getAwardName(award),
        winners,
        isComplete: winners.length > 0,
      };
    });
  };

  const getSideAwardsWonCountForContestant = (contestantId: number): number => {
    return (pageantAvailableAwards ?? []).reduce((count: number, award: any) => {
      const relationships = award?.awardRelationships ?? award?.registeredContestantAwardRelationships ?? [];

      const hasWon = relationships.some(
        (relationship: any) =>
          Number(relationship?.registeredPageantContestantId ?? 0) === Number(contestantId) &&
          relationship?.isWon === true,
      );

      return count + (hasWon ? 1 : 0);
    }, 0);
  };

  const timelineTypeOptions = useMemo(() => {
  const options = [
    {
      type: 'stage' as const,
      label: 'Stage Events',
      completion: stageCompletion,
    },
    {
      type: 'photo' as const,
      label: 'Photo Events',
      completion: photoCompletion,
    },
    {
      type: 'preCompetition' as const,
      label: 'Pre-Competition Events',
      completion: preCompetitionCompletion,
    },
  ];

  return options.filter((option) =>
    aocDashboardCards.some((timelineAoc) => {
      const rawAoc = sortedAocs.find(
        (item: any) => Number(item?.id) === Number(timelineAoc.id),
      );

      return getAocTimelineType(rawAoc) === option.type;
    }),
  );
}, [
  aocDashboardCards,
  sortedAocs,
  stageCompletion,
  photoCompletion,
  preCompetitionCompletion,
]);

const selectFirstAocForTimelineType = useCallback(
  (type: AocTimelineType) => {
    const firstAoc = aocDashboardCards.find((timelineAoc) => {
      const rawAoc = sortedAocs.find(
        (item: any) => Number(item?.id) === Number(timelineAoc.id),
      );

      return getAocTimelineType(rawAoc) === type;
    });

    if (firstAoc) {
      setExpandedAocIds({ [Number(firstAoc.id)]: true });
    }
  },
  [aocDashboardCards, sortedAocs],
);


  const renderAocDashboardOrg = () => {
    const hasExpandedAoc = Boolean(activeExpandedAoc);

    if (hasExpandedAoc && activeExpandedAoc) {
      const rawActiveAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(activeExpandedAoc.id));

      return (
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Card className="shadow-sm border-0 overflow-hidden">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-4">
                 {timelineTypeOptions.length > 1 && (
  <div className="d-flex flex-grow-1 border rounded overflow-hidden">
    {timelineTypeOptions.map((option) => (
      <Button
        key={option.type}
        className="flex-fill rounded-0 py-3"
        variant={aocTimelineType === option.type ? 'primary' : 'outline-primary'}
        onClick={() => {
          setAocTimelineType(option.type);
          selectFirstAocForTimelineType(option.type);
        }}
      >
        {option.label}
        {option.completion?.isComplete && (
          <Badge bg="success" className="ms-2">
            Complete
          </Badge>
        )}
      </Button>
    ))}
  </div>
)}

                  <Button variant="outline-danger py-3" title="Collapse View" onClick={() => setExpandedAocIds({})}>
                    ×
                  </Button>
                </div>

                <div className="d-flex align-items-center gap-3 mb-4">
                  <Button variant="outline-secondary" size="sm" onClick={goToPreviousAoc}>
                    ←
                  </Button>

                  <div className="flex-grow-1 overflow-auto position-relative px-2">
                    <div className="d-flex align-items-start gap-0 position-relative mb-3">
                      {filteredTimelineAocs.map((timelineAoc, index) => {
                        const isCurrent = Number(timelineAoc.id) === Number(activeExpandedAoc.id);
                        const isComplete = timelineAoc.percent >= 100;
                        const isLast = index === filteredTimelineAocs.length - 1;

                        return (
                          <button
                            ref={(element) => {
                              aocTimelineItemRefs.current[Number(timelineAoc.id)] = element;
                            }}
                            key={timelineAoc.id}
                            type="button"
                            className="btn p-0 border-0 bg-transparent text-center position-relative flex-fill"
                            style={{ minWidth: 115, zIndex: 2 }}
                            onClick={() => selectAocTimelineItem(Number(timelineAoc.id))}
                          >
                            {!isLast && (
                              <div
                                className="position-absolute"
                                style={{
                                  top: 29,
                                  left: '50%',
                                  width: '100%',
                                  height: 3,
                                  background: isComplete ? 'var(--success, #198754)' : 'rgba(128,128,128,.28)',
                                  zIndex: -1,
                                }}
                              />
                            )}

                        <div
  className={`mx-auto rounded-circle d-flex align-items-center justify-content-center ${
    isCurrent ? 'shadow-lg' : ''
  }`}
  style={{
    width: 58,
    height: 58,
    padding: isCurrent ? 0 : 4,
    background: isCurrent
      ? getRingColor(timelineAoc.percent)
      : `conic-gradient(${getRingColor(timelineAoc.percent)} ${
          timelineAoc.percent * 3.6
        }deg, rgba(128,128,128,.25) 0deg)`,
    border: isCurrent ? `3px solid ${getRingColor(timelineAoc.percent)}` : undefined,
  }}
>
  <div
    className={`rounded-circle d-flex align-items-center justify-content-center ${
      isCurrent ? '' : 'bg-foreground'
    }`}
    style={{
      width: isCurrent ? 58 : 48,
      height: isCurrent ? 58 : 48,
    }}
  >
    <div
      className={`fw-bold ${
        isCurrent ? 'text-white' : getPercentTextClassName(timelineAoc.percent)
      }`}
      style={{ fontSize: '.95rem', lineHeight: 1 }}
    >
      {timelineAoc.percent}%
    </div>
  </div>
</div>

                            <div
                              className={`small mt-2 text-truncate px-1 ${
                                isCurrent ? 'fw-bold text-body text-uppercase' : 'text-muted'
                              }`}
                            >
                              {timelineAoc.name}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Button variant="outline-secondary" size="sm" onClick={goToNextAoc}>
                    →
                  </Button>
                </div>

                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 border-top pt-3 mb-3">
                  <div>
                    <div className="fw-bold fs-3">
                      {activeExpandedAoc.name}
                      {getAocTypeName(rawActiveAoc) && (
                        <span className="small text-muted fw-normal ms-2">{getAocTypeName(rawActiveAoc)}</span>
                      )}
                    </div>

                    <div className="text-muted small">
                      {hasAgeGroups
                        ? 'Showing division and age group progress for this AOC.'
                        : 'Showing division progress for this AOC.'}
                    </div>
                  </div>
                </div>

                <Row className="g-3">
                  {activeExpandedAoc.divisions.map((division) => {
                    const divisionCompleted = hasAgeGroups
                      ? division.ageGroups.reduce(
                          (sum: number, ageGroup: any) => sum + Number(ageGroup.completed ?? 0),
                          0,
                        )
                      : Number(division.completed ?? 0);

                    const divisionTotal = hasAgeGroups
                      ? division.ageGroups.reduce((sum: number, ageGroup: any) => sum + Number(ageGroup.total ?? 0), 0)
                      : Number(division.total ?? 0);

                    const divisionPercent = divisionTotal === 0 ? 0 : Math.round((divisionCompleted / divisionTotal) * 100);

                    return (
                      <Col xs={12} key={`${activeExpandedAoc.id}-${division.id}`}>
                        <Card className="border shadow-none h-100">
                          <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <div className="fw-bold fs-5">{division.name}</div>
                                <div className="small text-muted">{divisionTotal} entered in this AOC</div>
                              </div>

                              <div className="text-end">
                                <div className={`${getPercentTextClassName(divisionPercent)} fw-bold fs-3`}>
                                  {divisionPercent}%
                                </div>
                                <div className="small text-muted">
                                  {divisionCompleted}/{divisionTotal}
                                </div>
                              </div>
                            </div>

                            {hasAgeGroups ? (
                              <div
                                className="d-grid gap-2"
                                style={{
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))',
                                }}
                              >
                                {division.ageGroups.map((ageGroup) => (
                                  <div
                                    key={`${activeExpandedAoc.id}-${division.id}-${String(ageGroup.id)}`}
                                    role="button"
                                    tabIndex={0}
                                    className="border rounded p-2 text-center bg-foreground cursor-pointer judge-progress-stat-box"
                                    onClick={() =>
                                      handleStartFromAocAgeGroup(
                                        activeExpandedAoc.id,
                                        division.id,
                                        ageGroup.ageGroupId || ageGroup.id,
                                      )
                                    }
                                  >
                                    <div className={`${getPercentTextClassName(ageGroup.percent)} lh-1 cta-3 fw-bold`}>
                                      {ageGroup.percent}%
                                    </div>

                                    <div className="small fw-bold text-body mt-2 text-truncate">{ageGroup.name}</div>

                                    <div className="text-muted mt-1" style={{ fontSize: '.72rem' }}>
                                      {ageGroup.completed}/{ageGroup.total} complete
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Button
                                variant="outline-primary"
                                className="w-100 py-3 fw-bold"
                                disabled={divisionTotal === 0}
                                onClick={() =>
                                  onStartScoring({
                                    aocId: activeExpandedAoc.id,
                                    divisionId: division.id,
                                  })
                                }
                              >
                                {divisionTotal === 0
                                  ? 'No entries in this division'
                                  : `Start ${activeExpandedAoc.name} - ${division.name}`}
                              </Button>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      );
    }

    return (
      <Row className="g-3 mb-4 row-cols-1 row-cols-md-2 row-cols-lg-3">
        {aocDashboardCards.map((aoc) => {
          const rawAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(aoc.id));
          const aocTypeName = getAocTypeName(rawAoc);
          const notCompleted = Math.max(0, Number(aoc.total) - Number(aoc.completed));

          return (
            <Col key={aoc.id}>
              <Card
                className="shadow-sm border-0 overflow-hidden cursor-pointer judge-progress-stat-box h-100"
                role="button"
                onClick={() => {
                  setAocTimelineType(getAocTimelineType(rawAoc));
                  setExpandedAocIds({ [Number(aoc.id)]: true });
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div className="min-w-0">
                      <div className="fw-bold fs-5 text-body text-truncate">{aoc.name}</div>

                      {aocTypeName && <div className="small text-muted text-truncate">{aocTypeName}</div>}

                      <div className="text-muted mt-2" style={{ fontSize: '.75rem' }}>
                        {aoc.total} entered · {aoc.completed} complete · {notCompleted} incomplete
                      </div>
                    </div>

                    <div
                      className={`${getPercentTextClassName(aoc.percent)} fw-bold text-end flex-shrink-0`}
                      style={{ fontSize: '2rem', lineHeight: 1 }}
                    >
                      {aoc.percent}%
                    </div>
                  </div>

                  <ProgressBar
                    variant={progressBarVariant(aoc.percent, aoc.total)}
                    now={aoc.percent}
                    className="mt-3"
                    style={{ height: 6 }}
                  />
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

const renderAocDashboard = () => {
  const hasExpandedAoc = Boolean(activeExpandedAoc);

  const getAocCompeteOrder = (aoc: any): number => {
    const raw =
      aoc?.competeOrder ??
      aoc?.competitionOrder ??
      aoc?.sortOrder ??
      aoc?.displayOrder ??
      aoc?.order ??
      Number.MAX_SAFE_INTEGER;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
  };

  const allTimelineAocs = [...aocDashboardCards].sort((a: any, b: any) => {
    const rawA = sortedAocs.find((item: any) => Number(item?.id) === Number(a.id));
    const rawB = sortedAocs.find((item: any) => Number(item?.id) === Number(b.id));

    const orderDelta = getAocCompeteOrder(rawA) - getAocCompeteOrder(rawB);
    if (orderDelta !== 0) return orderDelta;

    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
  });

  const openAoc = (aoc: any) => {
    const rawAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(aoc.id));
    setAocTimelineType(getAocTimelineType(rawAoc));
    setExpandedAocIds({ [Number(aoc.id)]: true });
  };

  const eventStatCards = (
    <Row className="g-3 mb-4 row-cols-1 row-cols-md-2 row-cols-lg-3">
      {allTimelineAocs.map((aoc) => {
        const rawAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(aoc.id));
        const aocTypeName = getAocTypeName(rawAoc);
        const notCompleted = Math.max(0, Number(aoc.total) - Number(aoc.completed));

        return (
          <Col key={aoc.id}>
            <Card
              className="shadow-sm border-0 overflow-hidden cursor-pointer judge-progress-stat-box h-100"
              role="button"
              onClick={() => openAoc(aoc)}
            >
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <div className="min-w-0">
                    <div className="fw-bold fs-5 text-body text-truncate">{aoc.name}</div>

                    {aocTypeName && <div className="small text-muted text-truncate">{aocTypeName}</div>}

                    <div className="text-muted mt-2" style={{ fontSize: '.75rem' }}>
                      {aoc.total} entered · {aoc.completed} complete · {notCompleted} incomplete
                    </div>
                  </div>

                  <div
                    className={`${getPercentTextClassName(aoc.percent)} fw-bold text-end flex-shrink-0`}
                    style={{ fontSize: '2rem', lineHeight: 1 }}
                  >
                    {aoc.percent}%
                  </div>
                </div>

                <ProgressBar
                  variant={progressBarVariant(aoc.percent, aoc.total)}
                  now={aoc.percent}
                  className="mt-3"
                  style={{ height: 6 }}
                />
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  if (!hasExpandedAoc || !activeExpandedAoc) {
    return eventStatCards;
  }

  const rawActiveAoc = sortedAocs.find((item: any) => Number(item?.id) === Number(activeExpandedAoc.id));

  return (
    <Row className="g-3 mb-4">
      <Col xs={12}>
        <Card className="shadow-sm border-0 overflow-hidden">
          <Card.Body className="p-3">
<div className="d-flex align-items-center gap-2 mb-4">
  <Button
    variant="outline-secondary"
    className="py-3 flex-shrink-0 btn-sm"
    onClick={() => setExpandedAocIds({})}
  >
    Back to All
  </Button>
</div>

<div className="d-flex align-items-center gap-3 mb-4">
  <Button variant="outline-secondary" size="sm" className="flex-shrink-0" onClick={goToPreviousAoc}>
    ←
  </Button>

  <div className="flex-grow-1 min-w-0 position-relative">
    <div className="flex-grow-1 overflow-auto position-relative px-2">
      <div
        className="d-flex align-items-start gap-0 position-relative mb-3"
        style={{
          minWidth: 'max-content',
        }}
      >
        {allTimelineAocs.map((timelineAoc, index) => {
          const isCurrent = Number(timelineAoc.id) === Number(activeExpandedAoc.id);
          const isComplete = timelineAoc.percent >= 100;
          const isLast = index === allTimelineAocs.length - 1;

          return (
            <button
              ref={(element) => {
                aocTimelineItemRefs.current[Number(timelineAoc.id)] = element;
              }}
              key={timelineAoc.id}
              type="button"
              className="btn p-0 border-0 bg-transparent text-center position-relative flex-fill"
              style={{ minWidth: 115, zIndex: 2 }}
              onClick={() => selectAocTimelineItem(Number(timelineAoc.id))}
            >
              {!isLast && (
                <div
                  className="position-absolute"
                  style={{
                    top: 29,
                    left: '50%',
                    width: '100%',
                    height: 3,
                    background: isComplete ? 'var(--success, #198754)' : 'rgba(128,128,128,.28)',
                    zIndex: -1,
                  }}
                />
              )}

              <div
                className={`mx-auto rounded-circle d-flex align-items-center justify-content-center ${
                  isCurrent ? 'shadow-lg' : ''
                }`}
                style={{
                  width: 58,
                  height: 58,
                  padding: isCurrent ? 0 : 4,
                  background: isCurrent
                    ? getRingColor(timelineAoc.percent)
                    : `conic-gradient(${getRingColor(timelineAoc.percent)} ${
                        timelineAoc.percent * 3.6
                      }deg, rgba(128,128,128,.25) 0deg)`,
                  border: isCurrent ? `3px solid ${getRingColor(timelineAoc.percent)}` : undefined,
                }}
              >
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center ${
                    isCurrent ? '' : 'bg-foreground'
                  }`}
                  style={{
                    width: isCurrent ? 58 : 48,
                    height: isCurrent ? 58 : 48,
                  }}
                >
                  <div
                    className={`fw-bold ${
                      isCurrent ? 'text-white' : getPercentTextClassName(timelineAoc.percent)
                    }`}
                    style={{ fontSize: '.95rem', lineHeight: 1 }}
                  >
                    {timelineAoc.percent}%
                  </div>
                </div>
              </div>

              <div
                className={`small mt-2 text-truncate px-1 ${
                  isCurrent ? 'fw-bold text-body text-uppercase' : 'text-muted'
                }`}
              >
                {timelineAoc.name}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  </div>

  <Button variant="outline-secondary" size="sm" className="flex-shrink-0" onClick={goToNextAoc}>
    →
  </Button>
</div>

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 border-top pt-3 mb-3">
              <div>
                <div className="fw-bold fs-3">
                  {activeExpandedAoc.name}
                  {getAocTypeName(rawActiveAoc) && (
                    <span className="small text-muted fw-normal ms-2">{getAocTypeName(rawActiveAoc)}</span>
                  )}
                </div>

                <div className="text-muted small">
                  {hasAgeGroups
                    ? 'Showing division and age group progress for this AOC.'
                    : 'Showing division progress for this AOC.'}
                </div>
              </div>
            </div>

            <Row className="g-3">
              {activeExpandedAoc.divisions.map((division) => {
                const divisionCompleted = hasAgeGroups
                  ? division.ageGroups.reduce((sum: number, ageGroup: any) => sum + Number(ageGroup.completed ?? 0), 0)
                  : Number(division.completed ?? 0);

                const divisionTotal = hasAgeGroups
                  ? division.ageGroups.reduce((sum: number, ageGroup: any) => sum + Number(ageGroup.total ?? 0), 0)
                  : Number(division.total ?? 0);

                const divisionPercent = divisionTotal === 0 ? 0 : Math.round((divisionCompleted / divisionTotal) * 100);

                return (
                  <Col
                    xs={12}
                    md={hasAgeGroups ? 12 : 6}
                    lg={hasAgeGroups ? 12 : 4}
                    key={`${activeExpandedAoc.id}-${division.id}`}
                  >
                    <Card className="border shadow-none h-100">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <div className="fw-bold fs-5">{division.name}</div>
                            <div className="small text-muted">{divisionTotal} entered in this AOC</div>
                          </div>

                          <div className="text-end">
                            <div className={`${getPercentTextClassName(divisionPercent)} fw-bold fs-3`}>
                              {divisionPercent}%
                            </div>
                            <div className="small text-muted">
                              {divisionCompleted}/{divisionTotal}
                            </div>
                          </div>
                        </div>

                        {hasAgeGroups ? (
                          <div
                            className="d-grid gap-2"
                            style={{
                              gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))',
                            }}
                          >
                            {division.ageGroups.map((ageGroup) => (
                              <div
                                key={`${activeExpandedAoc.id}-${division.id}-${String(ageGroup.id)}`}
                                role="button"
                                tabIndex={0}
                                className="border rounded p-2 text-center bg-foreground cursor-pointer judge-progress-stat-box"
                                onClick={() =>
                                  handleStartFromAocAgeGroup(
                                    activeExpandedAoc.id,
                                    division.id,
                                    ageGroup.ageGroupId || ageGroup.id,
                                  )
                                }
                              >
                                <div className={`${getPercentTextClassName(ageGroup.percent)} lh-1 cta-3 fw-bold`}>
                                  {ageGroup.percent}%
                                </div>

                                <div className="small fw-bold text-body mt-2 text-truncate">{ageGroup.name}</div>

                                <div className="text-muted mt-1" style={{ fontSize: '.72rem' }}>
                                  {ageGroup.completed}/{ageGroup.total} complete
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Button
                            variant={divisionPercent >= 100 ? 'success' : 'outline-primary'}
                            className="w-100 py-3 fw-bold"
                            disabled={divisionTotal === 0}
                            onClick={() =>
                              onStartScoring({
                                aocId: activeExpandedAoc.id,
                                divisionId: division.id,
                              })
                            }
                          >
                            {divisionTotal === 0
                              ? 'No entries in this division'
                              : `Start ${activeExpandedAoc.name} - ${division.name}`}
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

  const renderDivisionDashboard = () => (
    <Row className="g-3 mb-4 row-cols-1 row-cols-lg-2">
      {progressByDivision.map((division) => (
        <Col key={division.id}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                <div className="min-w-0">
                  <div className="fw-bold fs-5 text-body text-truncate">{division.name}</div>

                  <div className="text-muted small">
                    {division.contestants} contestants · {division.completed}/{division.total} judged events
                  </div>
                </div>

                <div
                  className={`${getPercentTextClassName(division.percent)} fw-bold text-end flex-shrink-0`}
                  style={{ fontSize: '1.8rem', lineHeight: 1 }}
                >
                  {division.percent}%
                </div>
              </div>

              {hasAgeGroups ? (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <tbody>
                      {division.ageGroups.map((ageGroup) => (
                        <tr key={`${division.id}-${String(ageGroup.id)}`}>
                          <td className="fw-bold small text-truncate" style={{ maxWidth: 120 }}>
                            {ageGroup.name}
                          </td>

                          <td style={{ width: '38%' }}>
                            <ProgressBar
                              now={ageGroup.percent}
                              variant={progressBarVariant(ageGroup.percent, ageGroup.total)}
                              style={{ height: 5 }}
                            />
                          </td>

                          <td
                            className={`${getPercentTextClassName(ageGroup.percent)} fw-bold text-end small`}
                            style={{ width: 48 }}
                          >
                            {ageGroup.percent}%
                          </td>

                          <td className="text-muted text-end" style={{ width: 72, fontSize: '.72rem' }}>
                            {ageGroup.completed}/{ageGroup.total}
                          </td>

                          <td className="text-muted text-end" style={{ width: 82, fontSize: '.72rem' }}>
                            {ageGroup.contestants} contestants
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="table-responsive mt-3">
                  <table className="table table-sm align-middle mb-0">
                    <tbody>
                      {sortedAocs.map((aoc: any) => {
                        const aocId = Number(aoc?.id ?? 0);
                        const progress = getDivisionAocProgress(aocId, division.id);

                        return (
                          <tr
                            key={`${division.id}-${aocId}`}
                            role="button"
                            style={{ cursor: progress.total > 0 ? 'pointer' : 'default' }}
                            onClick={() => {
                              if (progress.total > 0) {
                                onStartScoring({
                                  aocId,
                                  divisionId: division.id,
                                });
                              }
                            }}
                          >
                            <td className="fw-bold small text-truncate" style={{ maxWidth: 150 }}>
                              {aoc?.name ?? 'AOC'}
                            </td>

                            <td style={{ width: '38%' }}>
                              <ProgressBar
                                now={progress.percent}
                                variant={progressBarVariant(progress.percent, progress.total)}
                                style={{ height: 5 }}
                              />
                            </td>

                            <td
                              className={`${getPercentTextClassName(progress.percent)} fw-bold text-end small`}
                              style={{ width: 48 }}
                            >
                              {progress.percent}%
                            </td>

                            <td className="text-muted text-end" style={{ width: 72, fontSize: '.72rem' }}>
                              {progress.completed}/{progress.total}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const renderAgeGroupDashboard = () => {
    const divisionsWithAgeGroups = progressByDivision.filter((division) => division.ageGroups.length > 0);

    const activeDivision =
      divisionsWithAgeGroups.find((division) => Number(division.id) === Number(activeAgeGroupDivisionId)) ??
      divisionsWithAgeGroups[0] ??
      null;

    if (!activeDivision) {
      return (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-4 text-center text-muted">No age group progress available.</Card.Body>
        </Card>
      );
    }

    return (
      <>
        <Row className="g-3 mb-4 row-cols-1 row-cols-md-4">
          {divisionsWithAgeGroups.map((division) => {
            const isActive = Number(activeDivision.id) === Number(division.id);

            return (
              <Col key={division.id}>
                <Card
                  role="button"
                  className={`h-100 cursor-pointer judge-progress-stat-box ${
                    isActive ? 'border-primary shadow' : 'border-0 shadow-sm'
                  }`}
                  onClick={() => setActiveAgeGroupDivisionId(division.id)}
                >
                  <Card.Body className={isActive ? 'bg-primary text-white rounded p-3' : 'p-3'}>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="min-w-0">
                        <div className="fw-bold text-truncate">{division.name}</div>
                      </div>

                      <div className="fw-bold text-end flex-shrink-0" style={{ fontSize: '2rem', lineHeight: 1 }}>
                        {division.percent}%
                      </div>
                    </div>

                    <div className={isActive ? 'small text-white-50 mt-1' : 'small text-muted mt-1'}>
                      {division.ageGroups.length} age groups · {division.contestants} contestants
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-3">
            <div className="fw-bold fs-4 mb-3">
              {activeDivision.name}

              <ProgressBar
                now={activeDivision.percent}
                variant={progressBarVariant(activeDivision.percent, activeDivision.total)}
                className="mt-3"
                style={{ height: 5 }}
              />
            </div>

            <div className="d-grid gap-3">
              {ageGroupsOrdered.map((rawAgeGroup: any) => {
                const ageGroupId = Number(rawAgeGroup?.ageGroupId ?? rawAgeGroup?.id ?? 0);
                const ageGroupName = String(rawAgeGroup?.ageGroupName ?? rawAgeGroup?.name ?? rawAgeGroup?.label ?? 'Age Group');

                const matchingAgeGroup = activeDivision.ageGroups.find(
                  (ageGroup) =>
                    Number(ageGroup.ageGroupId || ageGroup.id) === ageGroupId || String(ageGroup.name) === ageGroupName,
                );

                if (!matchingAgeGroup) return null;

                const contestants = getAgeGroupContestantsForDivision(rawAgeGroup, activeDivision.id);
                const sideAwardRows = getSideAwardRowsForContestants(contestants);
                const sideAwardsComplete = sideAwardRows.length > 0 && sideAwardRows.every((award) => award.isComplete);

                const aocs = sortedAocs
                  .map((aoc: any) => {
                    const aocId = Number(aoc?.id ?? 0);
                    const progress = getAgeGroupAocProgress(
                      aocId,
                      activeDivision.id,
                      matchingAgeGroup.ageGroupId || matchingAgeGroup.id,
                    );

                    return {
                      aocId,
                      aocName: String(aoc?.name ?? 'AOC'),
                      ...progress,
                    };
                  })
                  .filter((aoc) => aoc.total > 0);

                return (
                  <Card key={`${activeDivision.id}-${ageGroupId || ageGroupName}`} className="border shadow-none">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="fw-bold fs-5 text-body text-truncate">{ageGroupName}</div>

                          <div className="text-muted small">
                            {contestants.length} contestants · {matchingAgeGroup.completed}/{matchingAgeGroup.total} judged events
                          </div>
                        </div>

                        <div className="text-end flex-shrink-0">
                          <div
                            className={`${getPercentTextClassName(matchingAgeGroup.percent)} fw-bold`}
                            style={{ fontSize: '1.8rem', lineHeight: 1 }}
                          >
                            {matchingAgeGroup.percent}%
                          </div>
                        </div>
                      </div>

                      <Row className="g-3 row-cols-1 row-cols-md-2">
                        <Col>
                          <div className="fw-bold small text-uppercase mb-2">AOC Progress</div>

                          <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                              <tbody>
                                {aocs.map((aoc: any) => (
                                  <tr
                                    key={`${activeDivision.id}-${ageGroupId}-${aoc.aocId}`}
                                    role="button"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      handleStartFromAocAgeGroup(
                                        aoc.aocId,
                                        activeDivision.id,
                                        matchingAgeGroup.ageGroupId || matchingAgeGroup.id,
                                      )
                                    }
                                  >
                                    <td className="fw-bold small text-truncate" style={{ maxWidth: 150 }}>
                                      {aoc.aocName}
                                    </td>

                                    <td style={{ width: '40%' }}>
                                      <ProgressBar
                                        now={aoc.percent}
                                        variant={progressBarVariant(aoc.percent, aoc.total)}
                                        style={{ height: 5 }}
                                      />
                                    </td>

                                    <td
                                      className={`${getPercentTextClassName(aoc.percent)} fw-bold text-end small`}
                                      style={{ width: 48 }}
                                    >
                                      {aoc.percent}%
                                    </td>

                                    <td className="text-muted text-end" style={{ width: 46, fontSize: '.72rem' }}>
                                      {aoc.completed}/{aoc.total}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Col>

                        <Col className="border rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="fw-bold text-uppercase">Side Awards</div>

                            <div className="d-flex align-items-center gap-2">
                              <Badge bg={sideAwardsComplete ? 'success' : 'warning'}>
                                {sideAwardsComplete ? 'COMPLETE' : 'INCOMPLETE'}
                              </Badge>

                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="py-1 px-2"
                                onClick={(event) => {
                                  event.stopPropagation();

                                  setSideAwardModal({
                                    show: true,
                                    divisionId: activeDivision.id,
                                    ageGroup: rawAgeGroup,
                                    contestants,
                                  });
                                }}
                              >
                                <CsLineIcons icon="edit" size={14} />
                              </Button>
                            </div>
                          </div>

                          {sideAwardRows.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-sm align-middle mb-0">
                                <tbody>
                                  {sideAwardRows.map((award) => (
                                    <tr key={award.awardId}>
                                      <td className="fw-bold small text-truncate" style={{ maxWidth: 130 }}>
                                        {award.awardName}
                                      </td>

                                      <td className="small text-muted text-truncate">
                                        {award.winners.length > 0 ? award.winners.join(', ') : 'No winner selected'}
                                      </td>

                                      <td className="text-end small fw-bold" style={{ width: 35 }}>
                                        {award.winners.length}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-muted small border rounded p-3">No side awards for this age group.</div>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        <Modal
          show={sideAwardModal.show}
          onHide={() =>
            setSideAwardModal({
              show: false,
              divisionId: 0,
              ageGroup: null,
              contestants: [],
            })
          }
          size="xl"
          centered
          animation={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Side Awards</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {sideAwardModal.ageGroup && (
              <JudgeSideAwardWinners
                pageantId={Number(pageant?.id ?? 0)}
                divisionId={sideAwardModal.divisionId}
                ageGroup={sideAwardModal.ageGroup}
                contestants={sideAwardModal.contestants}
                sideAwards={pageantAvailableAwards}
                onWinnerChange={async ({ registeredPageantContestantId, pageantAvailableTitleId, relationship }) => {
                  updateAwardRelationshipLocal?.(
                    {
                      registeredPageantContestantId,
                      pageantAvailableTitleId,
                    },
                    relationship,
                  );

                  return relationship;
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      </>
    );
  };

  const renderContestantDashboard = () => {
    const selectedDivisionId = contestantDivisionFilter ? Number(contestantDivisionFilter) : null;

    const ageGroupOptionsForSelectedDivision = ageGroupsOrdered
      .map((ageGroup: any) => {
        const ageGroupName = String(ageGroup?.ageGroupName ?? ageGroup?.name ?? ageGroup?.label ?? '');

        const hasContestantsInSelectedDivision =
          !selectedDivisionId || getAgeGroupContestantsForDivision(ageGroup, selectedDivisionId).length > 0;

        return { ageGroupName, hasContestantsInSelectedDivision };
      })
      .filter((ageGroup) => ageGroup.ageGroupName && ageGroup.hasContestantsInSelectedDivision);

    const visibleContestants = sortedDivisions.flatMap((division: any) => {
      const divisionId = getDivisionId(division);
      const divisionName = getDivisionName(division);

      if (selectedDivisionId && divisionId !== selectedDivisionId) return [];

      const divisionContestants = filteredContestants
        .filter((contestant) => contestant.divisionId === divisionId)
        .filter((contestant) => {
          if (contestantStatusFilter === 'complete') return contestant.percent >= 100;
          if (contestantStatusFilter === 'incomplete') return contestant.percent < 100;

          return true;
        });

      if (!hasAgeGroups) {
        return divisionContestants.map((contestant) => ({
          ...contestant,
          divisionName,
          ageGroupName: '',
          sideAwardsWonCount: getSideAwardsWonCountForContestant(contestant.id),
        }));
      }

      return ageGroupsOrdered.flatMap((ageGroup: any) => {
        const ageGroupName = String(ageGroup?.ageGroupName ?? ageGroup?.name ?? ageGroup?.label ?? '');

        return divisionContestants
          .filter((contestant) => contestant.ageGroupName === ageGroupName)
          .map((contestant) => ({
            ...contestant,
            divisionName,
            ageGroupName,
            sideAwardsWonCount: getSideAwardsWonCountForContestant(contestant.id),
          }));
      });
    });

    const completeCount = progressByContestant.filter((contestant) => contestant.percent >= 100).length;
    const incompleteCount = progressByContestant.filter((contestant) => contestant.percent < 100).length;

    return (
      <>
        <Row className="g-3 mb-4 row-cols-1 row-cols-md-2">
          <Col>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3">
                <div className="text-muted small">Contestants Complete</div>
                <div className="fw-bold text-success" style={{ fontSize: '2rem', lineHeight: 1 }}>
                  {completeCount}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3">
                <div className="text-muted small">Contestants Incomplete</div>
                <div className="fw-bold text-warning" style={{ fontSize: '2rem', lineHeight: 1 }}>
                  {incompleteCount}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <Row className="g-2">
              <Col md={hasAgeGroups ? 4 : 5}>
                <Form.Control
                  placeholder="Search by contestant name or number..."
                  value={contestantSearch}
                  onChange={(e) => setContestantSearch(e.target.value)}
                />
              </Col>

              <Col md={2}>
                <Form.Select
                  value={contestantDivisionFilter}
                  onChange={(e) => {
                    setContestantDivisionFilter(e.target.value);
                    setContestantAgeGroupFilter('');
                  }}
                >
                  <option value="">All Divisions</option>

                  {sortedDivisions.map((division: any) => (
                    <option key={getDivisionId(division)} value={String(getDivisionId(division))}>
                      {getDivisionName(division)}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {hasAgeGroups && (
                <Col md={2}>
                  <Form.Select value={contestantAgeGroupFilter} onChange={(e) => setContestantAgeGroupFilter(e.target.value)}>
                    <option value="">All Age Groups</option>

                    {ageGroupOptionsForSelectedDivision.map((ageGroup) => (
                      <option key={ageGroup.ageGroupName} value={ageGroup.ageGroupName}>
                        {ageGroup.ageGroupName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              <Col md={3}>
                <div className="btn-group w-100">
                  <Button
                    variant={contestantStatusFilter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setContestantStatusFilter('all')}
                  >
                    All
                  </Button>

                  <Button
                    variant={contestantStatusFilter === 'complete' ? 'primary' : 'outline-primary'}
                    onClick={() => setContestantStatusFilter('complete')}
                  >
                    Complete
                  </Button>

                  <Button
                    variant={contestantStatusFilter === 'incomplete' ? 'primary' : 'outline-primary'}
                    onClick={() => setContestantStatusFilter('incomplete')}
                  >
                    Incomplete
                  </Button>
                </div>
              </Col>

              <Col md={1}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => {
                    setContestantSearch('');
                    setContestantDivisionFilter('');
                    setContestantAgeGroupFilter('');
                    setContestantStatusFilter('all');
                  }}
                >
                  X
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Number</th>
                    <th>Contestant</th>
                    <th>Division</th>
                    {hasAgeGroups && <th>Age Group</th>}
                    <th style={{ width: 115 }}>Status</th>
                    <th style={{ width: 90 }}>Progress</th>
                    <th style={{ width: 110 }}>Side Awards</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleContestants.map((contestant: any, index: number) => {
                    const previous = visibleContestants[index - 1];

                    const isNewDivision = !previous || previous.divisionName !== contestant.divisionName;

                    const isNewAgeGroup =
                      hasAgeGroups &&
                      (!previous ||
                        previous.divisionName !== contestant.divisionName ||
                        previous.ageGroupName !== contestant.ageGroupName);

                    return (
                      <tr
                        key={contestant.id}
                        style={{
                          borderTop: isNewDivision
                            ? '3px solid rgba(0,0,0,.18)'
                            : isNewAgeGroup
                              ? '2px solid rgba(0,0,0,.1)'
                              : undefined,
                        }}
                      >
                        <td className="fw-bold">#{contestant.contestantNumber}</td>

                        <td>
                          <div className="fw-bold">{contestant.fullName}</div>
                        </td>

                        <td className="small text-muted">{contestant.divisionName}</td>

                        {hasAgeGroups && <td className="small text-muted">{contestant.ageGroupName}</td>}

                        <td>
                          {contestant.percent >= 100 ? (
                            <Badge bg="success">COMPLETE</Badge>
                          ) : contestant.percent > 0 ? (
                            <Badge bg="warning">INCOMPLETE</Badge>
                          ) : (
                            <Badge bg="secondary">NOT STARTED</Badge>
                          )}
                        </td>

                        <td>
                          <div className={`fw-bold ${getPercentTextClassName(contestant.percent)}`}>{contestant.percent}%</div>
                        </td>

                        <td className="text-center fw-bold">
                          {contestant.sideAwardsWonCount > 0 ? contestant.sideAwardsWonCount : ''}
                        </td>
                      </tr>
                    );
                  })}

                  {visibleContestants.length === 0 && (
                    <tr>
                      <td colSpan={hasAgeGroups ? 7 : 6} className="text-center text-muted py-4">
                        No contestants match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </>
    );
  };

  const renderActiveDashboard = () => {
    if (activeTab === 'division') return renderDivisionDashboard();
    if (activeTab === 'ageGroup') return renderAgeGroupDashboard();
    if (activeTab === 'contestants') return renderContestantDashboard();

    return renderAocDashboard();
  };

  const content = (
    <div className="w-100 judge-event-home pb-8">
      <style>{`
        .judge-progress-stat-box {
          transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
          cursor: pointer;
        }

        .judge-progress-stat-box:hover {
          transform: translateY(-1px);
          box-shadow: 0 .5rem 1rem rgba(0, 0, 0, .08);
          border-color: rgba(var(--primary-rgb, 13, 110, 253), .35) !important;
        }

        .judge-aoc-collapse-button {
          border-radius: .75rem;
        }

        .judge-aoc-collapse-button:hover {
          background: rgba(0, 0, 0, .02);
        }

        .judge-aoc-chevron {
          transition: transform 140ms ease;
        }

        .judge-aoc-chevron-expanded {
          transform: rotate(180deg);
        }
      `}</style>

      <Modal
        show={showWelcomeModal}
        centered
        backdrop="static"
        keyboard={false}
        animation={false}
        size="lg"
        onHide={() => setShowWelcomeModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Welcome {judge.judgeLabel} to the {judge.eventName} Pageant!
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-4 border-1 border-danger p-2">
           <p className="text-muted mb-4 fw-bold">
            Step 1, please test your tablet signal!
          </p>
          <TestJudgeTablet
  className="mb-3"
  onTestPassed={() => setTabletTestPassed(true)}
/>
          <p className="text-muted mb-4">
            Before scoring, please confirm your real name and read through and accept the judging terms and conditions.
          </p>
</div>
<hr></hr>
<div className="mb-4  p-2">
           <p className="text-muted mb-4 fw-bold">
            Step 2, please confirm your real name and accept the judging terms and conditions!
          </p>


          <Form.Group className="mb-4">
            <Form.Label>Judge Name</Form.Label>
            <Form.Control
              value={welcomeJudgeName}
              onChange={(event) => setWelcomeJudgeName(event.target.value)}
              placeholder="Enter your judge name"
            />
          </Form.Group>

          <div className="border rounded bg-light text-body p-3 mb-4" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <h5 className="mb-3">Terms and Conditions</h5>
            <ol className="mb-0 ps-3">
              <li className="mb-2">
                I understand that all scoring decisions must be fair, unbiased, and based only on the official scoring criteria.
              </li>
              <li className="mb-2">I agree to keep contestant scores, rankings, notes, and event information confidential.</li>
              <li className="mb-2">
                I understand that I should not discuss scoring with contestants, families, coaches, or other unauthorized parties during the event.
              </li>
              <li className="mb-2">I agree to submit complete and accurate scores to the best of my ability.</li>
              <li className="mb-2">I understand that once submitted, my scores may be used to calculate official event results.</li>
              <li className="mb-2">I will notify event staff immediately if I experience a technical issue or conflict of interest.</li>
            </ol>
          </div>

          <Form.Check
            type="checkbox"
            id="judge-accept-terms"
            checked={acceptedTerms}
            onChange={(event) => setAcceptedTerms(event.target.checked)}
            label="I have read and accept the judging terms and conditions."
          />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="success"
            disabled={!welcomeJudgeName.trim() || !acceptedTerms || isSavingWelcome}
            onClick={handleAcceptWelcome}
          >
            {isSavingWelcome ? 'Saving...' : 'Accept Terms and Continue'}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="container-fluid p-3 p-md-4">
        {overallProgress.percent === 100 && (
          <Row className="g-3 mb-3">
            <Col xs={12}>
              <Alert variant="success" className="mb-0 text-center fw-bold d-flex align-items-center justify-content-center gap-2">
                <CsLineIcons icon="check-circle" size={22} />
                <span>YOU ARE 100% COMPLETE!</span>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-2">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                  <div>
                    {isEditingJudgeName ? (
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Form.Control
                          size="sm"
                          value={judgeNameDraft}
                          onChange={(event) => setJudgeNameDraft(event.target.value)}
                          placeholder="Judge name"
                          className="w-auto"
                        />

                        <Button
                          size="sm"
                          variant="success"
                          disabled={!judgeNameDraft.trim() || isSavingJudgeName}
                          onClick={handleSaveJudgeName}
                        >
                          {isSavingJudgeName ? 'Saving...' : 'Save'}
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={isSavingJudgeName}
                          onClick={() => {
                            setJudgeNameDraft(String(judge?.judgeName ?? ''));
                            setIsEditingJudgeName(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="mb-2 d-flex align-items-center gap-2 cta-3">
                        <span>Welcome, {welcomeJudgeName || judge.judgeName || 'Judge'}</span>

                        <Button
                          variant="link"
                          className="p-0 text-muted"
                          title="Edit judge name"
                          onClick={() => {
                            setJudgeNameDraft(String(welcomeJudgeName || judge?.judgeName || ''));
                            setIsEditingJudgeName(true);
                          }}
                        >
                          <CsLineIcons icon="edit" size={16} />
                        </Button>
                      </div>
                    )}

                    <div className="text-muted">
                      <div>
                        <strong>Event:</strong> {judge.eventName}
                      </div>
                      <div>
                        <strong>Judge Label:</strong> {judge.judgeLabel}
                      </div>
                      <div>
                        <strong>Judge Access Code:</strong> {judge.accessCodeLast4}
                      </div>
                      <div>
                        <strong>Judge Status:</strong>{' '}
                        <Badge bg={String(judge?.status ?? '').toLowerCase() === 'inactive' ? 'danger' : 'info'} className="text-uppercase">
                          {judge?.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="text-start text-md-end">
                    <div
                      className={overallProgress.percent === 100 ? 'fs-1 fw-bold text-success' : 'fs-1 fw-bold text-primary'}
                    >
                      {overallProgress.percent}%
                    </div>
                    <div className="text-muted small">
                      Overall Complete ({overallProgress.completed}/{overallProgress.total})
                    </div>
                    <Button variant="outline-primary" className="py-2 btn-sm" onClick={handleExportJudgeScores}>
                      <CsLineIcons icon="download" size={16} className="me-2" />
                      Export Scores
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className={`g-3 mb-4 row-cols-2 ${hasAgeGroups ? 'row-cols-md-4' : 'row-cols-md-3'}`}>
          <Col>
            <div
              role="button"
              tabIndex={0}
              className={statBoxClassName('aoc')}
              onClick={() => setActiveTab('aoc')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setActiveTab('aoc');
              }}
            >
              <div className={activeTab === 'aoc' ? 'small mb-1 text-white-50' : 'text-muted small mb-1'}>
                Areas of Competition
              </div>
              <div className="fs-4 fw-bold">{totalAocs}</div>
            </div>
          </Col>

          <Col>
            <div
              role="button"
              tabIndex={0}
              className={statBoxClassName('division')}
              onClick={() => setActiveTab('division')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setActiveTab('division');
              }}
            >
              <div className={activeTab === 'division' ? 'small mb-1 text-white-50' : 'text-muted small mb-1'}>
                Divisions
              </div>
              <div className="fs-4 fw-bold">{totalDivisions}</div>
            </div>
          </Col>

          {hasAgeGroups && (
            <Col>
              <div
                role="button"
                tabIndex={0}
                className={statBoxClassName('ageGroup')}
                onClick={() => setActiveTab('ageGroup')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setActiveTab('ageGroup');
                }}
              >
                <div className={activeTab === 'ageGroup' ? 'small mb-1 text-white-50' : 'text-muted small mb-1'}>
                  Age Groups
                </div>
                <div className="fs-4 fw-bold">{totalAgeGroups}</div>
              </div>
            </Col>
          )}

          <Col>
            <div
              role="button"
              tabIndex={0}
              className={statBoxClassName('contestants')}
              onClick={() => setActiveTab('contestants')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setActiveTab('contestants');
              }}
            >
              <div className={activeTab === 'contestants' ? 'small mb-1 text-white-50' : 'text-muted small mb-1'}>
                Contestants
              </div>
              <div className="fs-4 fw-bold">{totalContestants}</div>
            </div>
          </Col>
        </Row>

        {renderActiveDashboard()}
      </div>

      <div className="judge-fixed-footer">
        <div className="container-fluid p-3">
          <Button size="lg" variant="info" className="w-100 py-3 fw-bold fs-1" onClick={openStartScoringPrompt}>
            START SCORING
          </Button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" className="mb-3" />
          <div className="text-muted">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <LayoutJudgePortal judgeView="home" headerState="">
      <>
        {content}
        {startScoringSelectionModal}
      </>
    </LayoutJudgePortal>
  );
};

export default JudgeEventHome;